/** @jsxImportSource @emotion/react */
"use client";

import type { CSSObject } from "@emotion/react";
import { ClassNames } from "@emotion/react";
import { useLocale } from "@i18n/react";
import type { Locale } from "date-fns";
import { format } from "date-fns";
import { de, enUS, es, fr, it, pl, pt } from "date-fns/locale";
import { useState } from "react";
import type { DayPickerLocale } from "react-day-picker";
import {
  DayFlag,
  DayPicker,
  getDefaultClassNames,
  SelectionState,
  UI,
} from "react-day-picker";

import type { ThemeType } from "../../../../../global/emotion";
import { numericPixels, radius } from "../../../../../global/emotion";
import { cn } from "../../../../utils/cn";
import { MonthSelector } from "./MonthSelector.emotion";

const cssMonthGrid = (): CSSObject => ({
  marginInline: "auto",
});

const cssMonthCaption: CSSObject = {
  display: "none",
};

const cssWeekday = (theme: ThemeType): CSSObject => ({
  width: numericPixels[48],
  height: numericPixels[48],
  fontSize: numericPixels[14],
  fontWeight: 700,
  lineHeight: numericPixels[18],
  color: theme.semanticColors.neutralsMedium,
  textAlign: "center",
  verticalAlign: "middle",
});

const cssDay: CSSObject = {
  width: numericPixels[48],
  height: numericPixels[48],
  padding: 0,
  borderRadius: radius.m,
};

const cssDayButton = (theme: ThemeType): CSSObject => ({
  width: numericPixels[48],
  height: numericPixels[48],
  borderRadius: radius.full,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: numericPixels[14],
  fontWeight: 700,
  lineHeight: numericPixels[18],
  color: "inherit",
  "&:disabled": {
    cursor: "not-allowed",
  },
  "&:hover:not(:disabled)": {
    background: theme.semanticColors.neutralsVeryLow,
    borderRadius: radius.full,
  },
  ".rdp-today &": {
    borderRadius: radius.full,
    background: theme.semanticColors.accentMedium,
    color: "white",
  },
  ".rdp-today.rdp-hidden &": {
    background: "transparent",
    borderRadius: radius.m,
    color: "inherit",
  },
  ".rdp-selected &:hover:not(:disabled)": {
    background: "transparent",
  },
  ".rdp-today &:hover:not(:disabled)": {
    background: theme.semanticColors.accentMedium,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.semanticColors.accentMedium}`,
    outlineOffset: -2,
  },
});

const cssSelected = (theme: ThemeType): CSSObject => ({
  background: theme.semanticColors.primary,
  color: theme.semanticColors.secondary,
  borderRadius: radius.m,
  "&.rdp-hidden": {
    background: "transparent",
  },
  "&.rdp-today": {
    background: "transparent",
  },
});

// Dot underneath the day number via ::after pseudo-element
const cssDot = (theme: ThemeType): CSSObject => ({
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: numericPixels[6],
    left: "50%",
    transform: "translateX(-50%)",
    width: numericPixels[6],
    height: numericPixels[6],
    borderRadius: radius.full,
    background: theme.semanticColors.accentMedium,
  },
  "&.rdp-today::after": {
    background: theme.constantSemanticColors.onColorHighest,
  },
  "&.rdp-hidden::after": {
    display: "none",
  },
});

const cssDisabled = (): CSSObject => ({
  opacity: 0.4,
  cursor: "not-allowed",
});

const localeCodeToDateFns: Record<string, Locale> = {
  de,
  en: enUS,
  "en-PH": enUS,
  es,
  fr,
  it,
  pl,
  pt,
};

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  options?: { displayDot?: (date: Date) => boolean };
};

export const Calendar = ({ options, ...props }: CalendarProps) => {
  const [month, setMonth] = useState(props.defaultMonth ?? new Date());
  const defaultClassNames = getDefaultClassNames();
  const localeCode = useLocale();
  const dayPickerLocale =
    localeCodeToDateFns[localeCode] ?? (enUS as unknown as DayPickerLocale);

  return (
    <ClassNames>
      {({ css, theme }) => {
        const t = theme as ThemeType;
        const dayButton = cssDayButton(t);

        return (
          <div>
            <MonthSelector currentMonth={month} onMonthChange={setMonth} />
            <DayPicker
              {...props}
              month={month}
              onMonthChange={setMonth}
              {...(["en", "en-PH"].includes(localeCode) && {
                formatters: {
                  formatWeekdayName: (date) =>
                    format(date, "EEEEE", { locale: enUS }),
                },
              })}
              locale={dayPickerLocale}
              modifiers={{ hasDot: options?.displayDot }}
              modifiersClassNames={{ hasDot: css(cssDot(t)) }}
              classNames={{
                [UI.MonthCaption]: css(cssMonthCaption),
                [UI.MonthGrid]: css(cssMonthGrid()),
                [UI.Weekday]: css(cssWeekday(t)),
                [UI.Day]: css(cssDay),
                [UI.DayButton]: css(dayButton),
                [DayFlag.disabled]: css(cssDisabled()),
                [SelectionState.selected]: cn(
                  css(cssSelected(t)),
                  defaultClassNames[SelectionState.selected],
                ),
              }}
              components={{
                Nav: () => null,
              }}
            />
          </div>
        );
      }}
    </ClassNames>
  );
};
